import React, { Dispatch, ReactNode, useEffect, useState } from 'react';
import { Set } from 'immutable';
import cn from 'classnames';
import Modal, { ModalButton } from '@components/Modals';
import styles from './RequestMissingBirthdays.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import { selectBirthdayRequestRecommendations } from '@reducers/data/birthdays';
import { ID } from '@app/types';
import { Checkbox } from '@components/FormInputs';
import ContactAvatar from '@components/ContactAvatar';
import { pluralize } from '@support/string';
import Contact from '@models/Contact';
import { selectActiveModalDispatchOrigin } from '@actions/ui';
import { requestBirthdays } from '@actions/birthdays';
import analytics from '@support/analytics';

const SelectContactsModal = ({
  isOpen,
  closeModal,
  recommendedContacts,
  selectedContacts,
  setSelectedContacts,
  onConfirmationClick,
}: {
  isOpen: boolean;
  closeModal: () => void;
  recommendedContacts: Contact[];
  selectedContacts: Set<ID>;
  setSelectedContacts: Dispatch<Set<ID>>;
  onConfirmationClick: () => void;
}) => {
  const [hoveredLabel, setHoveredLabel] = useState<string | null>(null);
  const modalSource = useSelector(selectActiveModalDispatchOrigin);

  let copy: ReactNode =
    "Birthday wishes are a great way to be thoughtful. You're currently missing birthdays for some close relationships. Should we email them to request and then update their birthday?";

  if (modalSource === 'userCallout') {
    copy = (
      <>
        {copy} You can find these suggestions at any time by going to{' '}
        <span className={styles.highlight}>Settings &gt; Info &gt; Request Missing Birthdays</span>.
      </>
    );
  }

  useEffect(() => {
    analytics.track('Missing Birthdays Viewed');
  }, []);

  return (
    <Modal className={styles.modal} isOpen={isOpen} onRequestClose={closeModal}>
      <div className={cn(styles.header, 'modal-header')}>Request Missing Birthdays</div>
      <div className={styles.contents}>
        <p className={styles.copy}>{copy}</p>
        <ul className={styles.recommendations}>
          <li className={styles.optionRow}>
            <Checkbox
              id="select-all"
              checked={selectedContacts.size === recommendedContacts.length}
              onChange={() => {
                if (selectedContacts.size === recommendedContacts.length) {
                  setSelectedContacts(Set());
                } else {
                  setSelectedContacts(Set(recommendedContacts.map((contact) => contact.id)));
                }
              }}
              hovered={hoveredLabel === 'select-all'}
            />
            <label
              className={styles.selectAllLabel}
              htmlFor="select-all"
              onMouseEnter={() => setHoveredLabel('select-all')}
              onMouseLeave={() => setHoveredLabel(null)}
            >
              Select All
            </label>
          </li>
          {recommendedContacts.map((contact) => (
            <li key={contact.id} className={styles.optionRow}>
              <Checkbox
                id={contact.id}
                checked={selectedContacts.has(contact.id)}
                onChange={() => {
                  if (selectedContacts.has(contact.id)) {
                    setSelectedContacts(selectedContacts.delete(contact.id));
                  } else {
                    setSelectedContacts(selectedContacts.add(contact.id));
                  }
                }}
                hovered={hoveredLabel === contact.id}
              />
              <label
                className={styles.contactName}
                htmlFor={contact.id}
                onMouseEnter={() => setHoveredLabel(contact.id)}
                onMouseLeave={() => setHoveredLabel(null)}
              >
                {contact.name}
              </label>
              <ContactAvatar contact={contact} size="sm" />
            </li>
          ))}
        </ul>
      </div>
      <div className={cn('modal-button-row', styles.btns)}>
        <ModalButton onClick={closeModal} className={styles.cancelBtn} variant="muted">
          Cancel
        </ModalButton>
        <ModalButton
          onClick={onConfirmationClick}
          className={styles.cancelBtn}
          variant="primary"
          disabled={selectedContacts.size === 0}
        >
          Send Message
        </ModalButton>
      </div>
    </Modal>
  );
};

const ConfirmBirthdayRequestsModal = ({
  isOpen,
  closeModal,
  contactCount,
  onConfirmationClick,
  onGoBackClick,
}) => (
  <Modal className="modal-small" isOpen={isOpen} onRequestClose={closeModal}>
    <div className="modal-header">Confirmation</div>
    <div className="modal-content-padded">
      <p className={styles.copy}>
        We'll email {contactCount} {pluralize(contactCount, 'person')} on your behalf.
      </p>
      <div className="modal-button-row">
        <ModalButton variant="muted" onClick={onGoBackClick} tabIndex={0}>
          Go back
        </ModalButton>
        <ModalButton variant="primary" onClick={onConfirmationClick} autoFocus tabIndex={0}>
          Send {pluralize(contactCount, 'message')}
        </ModalButton>
      </div>
    </div>
  </Modal>
);

export default function RequestMissingBirthdaysModal({ closeModal }: { closeModal: () => void }) {
  const dispatch = useDispatch();
  const birthdayRequestRecommendations = useSelector(selectBirthdayRequestRecommendations);
  const [confirmingEmailSend, setConfirmingEmailSend] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState<Set<ID>>(
    Set(birthdayRequestRecommendations.map((contact) => contact.id)),
  );

  const sendBirthdayRequestEmails = () => {
    dispatch(requestBirthdays({ contactIDs: selectedContacts.valueSeq().toArray() }));
    closeModal();
  };

  return (
    <>
      <SelectContactsModal
        isOpen={!confirmingEmailSend}
        closeModal={closeModal}
        recommendedContacts={birthdayRequestRecommendations}
        selectedContacts={selectedContacts}
        setSelectedContacts={setSelectedContacts}
        onConfirmationClick={() => setConfirmingEmailSend(true)}
      />
      <ConfirmBirthdayRequestsModal
        isOpen={confirmingEmailSend}
        closeModal={closeModal}
        contactCount={selectedContacts.size}
        onConfirmationClick={sendBirthdayRequestEmails}
        onGoBackClick={() => setConfirmingEmailSend(false)}
      />
    </>
  );
}
